import React, { useCallback, useEffect, useMemo, useState } from "react";
import SlotCell from "./SlotCell";
import { ACTIVITIES } from "../../config/activities";
import { buildBoardColumn, generateUpcomingSlots } from "../../lib/infoSlots";
import { minutesToTime } from "../../lib/slots";

const CLOCK_TICK_MS = 1000;
const BOARD_REFRESH_MS = 10000;
const BOARD_STEP_MINUTES = 10;
const SCANNED_ENDPOINT = "/backend/get_scanned_data.php";

const emptyBoard = () => Object.fromEntries(ACTIVITIES.map((activity) => [activity.key, []]));

const upcomingSlotsFor = (now) =>
  Object.fromEntries(
    ACTIVITIES.map((activity) => [
      activity.key,
      generateUpcomingSlots(now, activity.slotStepMinutes, activity.breakMinute),
    ])
  );

/** Live occupancy board for the event day: the next few slots of every activity with team check-ins. */
const InfoPage = () => {
  const [now, setNow] = useState(() => new Date());
  const [board, setBoard] = useState(emptyBoard);

  // Changes every 10 minutes and triggers a reload so the board rolls forward with the clock.
  const slotKey = useMemo(() => {
    const minutes = now.getHours() * 60 + now.getMinutes();
    return minutesToTime(Math.floor(minutes / BOARD_STEP_MINUTES) * BOARD_STEP_MINUTES);
  }, [now]);

  const loadBoard = useCallback(async () => {
    const upcoming = upcomingSlotsFor(new Date());
    try {
      const responses = await Promise.all([
        ...ACTIVITIES.map((activity) => fetch(activity.api.availability)),
        fetch(SCANNED_ENDPOINT),
      ]);
      const payloads = await Promise.all(responses.map((response) => response.json()));
      const scannedPayload = payloads.pop();
      const scannedRows = scannedPayload?.success ? scannedPayload.data : [];
      setBoard(
        Object.fromEntries(
          ACTIVITIES.map((activity, index) => [
            activity.key,
            buildBoardColumn(upcoming[activity.key], payloads[index], scannedRows, activity.tableNumber),
          ])
        )
      );
    } catch (error) {
      console.error("Error fetching board data:", error);
      setBoard(
        Object.fromEntries(
          ACTIVITIES.map((activity) => [activity.key, buildBoardColumn(upcoming[activity.key], null, [], activity.tableNumber)])
        )
      );
    }
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), CLOCK_TICK_MS);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    loadBoard();
  }, [loadBoard, slotKey]);

  useEffect(() => {
    const timer = setInterval(loadBoard, BOARD_REFRESH_MS);
    return () => clearInterval(timer);
  }, [loadBoard]);

  const rowCount = Math.max(0, ...ACTIVITIES.map((activity) => board[activity.key].length));
  const rows = Array.from({ length: rowCount }, (_, index) => index);
  const clock = now.toLocaleTimeString("cs-CZ", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });

  return (
    <>
      <div className="rhead">
        <span className="kicker">Informace o rezervacích</span>
        <h1 className="display-h1">OBSAZENOST</h1>
        <p className="lead">
          Aktuální čas: <span className="clock">{clock}</span>
        </p>
      </div>

      <div className="board">
        <table>
          <thead>
            <tr>
              {ACTIVITIES.map((activity) => (
                <th key={activity.key} scope="col">
                  {activity.shortName}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((index) => (
              <tr key={index}>
                {ACTIVITIES.map((activity) => (
                  <SlotCell key={activity.key} slot={board[activity.key][index]} />
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="legend mb-14">
        <span className="open">volná místa</span>
        <span className="full">obsazeno</span>
        <span className="scanned">naskenováno</span>
      </div>
    </>
  );
};

export default InfoPage;
